import { ITracking, ITrackingConstructorArgs, selectFinalIndex, TrackingEngine } from 'components-machine';
import { getQuestionGroupId, getQuestionSetId, selectQuestionIndex } from 'components-machine/src/selects';
import { FILE_TYPE, PIXEL_VERSION, RATING_TYPE, SchemaEngine } from 'schema';
import { Tracking as BaseTracking } from 'tracking';
import { Poll } from '../types/poll.domain';
import { isAuthQuestion } from '../utils/guards';

export class Tracking extends BaseTracking implements ITracking {
  private readonly trackingEngine: TrackingEngine;
  private readonly schemaEngine: SchemaEngine;
  private poll: Poll; // Может изменяться для опросов с серверным прохождением

  constructor(...[referer, getParams, trackingEngine, poll, { pollsIc }, schemaEngine]: ITrackingConstructorArgs) {
    super({
      ...poll,
      referer,
      pollsIc,
      getParams,
      env: poll.env,
      wave: poll.wave,
      revision: poll.revision,
      questionCount: poll.questions.length,
      authProvider: pollAuthProvider(poll),
      pixelVersion: PIXEL_VERSION,
    });

    this.trackingEngine = trackingEngine;
    this.schemaEngine = schemaEngine;
    this.poll = poll;
  }

  public setPoll(poll: Poll | null) {
    if (poll) {
      this.setAuthProvider(pollAuthProvider(poll));
      this.poll = poll;
    }
  }

  public override async answer(
    {
      questionId,
      answersIds = [],
      answersOrder = [],
      answeredIds = [],
      answeredOthers,
      awardAmount,
      elapsedTime,
      isTimeExpired,
      previousHash,
      currentHash,
    }: {
      questionId: string;
      answersIds?: string[];
      answersOrder?: number[];
      answeredIds?: string[];
      answeredOthers?: string;
      awardAmount?: number;
      elapsedTime?: number | null;
      isTimeExpired?: boolean;
      currentHash: string | null;
      previousHash: string | null;
    },
    retryIds?: string[],
  ) {
    const { trackingEngine, schemaEngine, poll } = this;

    const questionIndex = selectQuestionIndex(poll, questionId);
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const question = poll.questions[questionIndex]!;

    const { deckId } = question;
    const groupId = getQuestionGroupId(question, poll.questionDecks);
    const setId = getQuestionSetId(question, poll);

    const answers = 'answers' in question.data ? question.data.answers : [];
    const safeAnsweredOthers = await trackingEngine.questionReplaceUnsafeInput(question, answeredOthers);

    return super.answer(
      {
        setId,
        groupId,
        deckId,
        questionId,
        questionType: question.data.type,
        questionText: question.data.text || (question.data as { description?: string }).description || '',
        questionIndex,
        answers,
        answersIds,
        answeredIds,
        answersOrder,
        awardAmount,
        elapsedTime,
        isTimeExpired,
        answeredOthers: safeAnsweredOthers,
        currentHash,
        previousHash,
        transform: data => {
          if (schemaEngine.hasTrait(question.data.type, FILE_TYPE)) {
            data.file_is_uploaded = answeredOthers?.length ? 1 : 0;
          }

          if (schemaEngine.hasTrait(question.data.type, RATING_TYPE)) {
            const [ratingValue] = answeredIds;
            const rating = ratingValue && Number(ratingValue);
            if (typeof rating === 'number' && !Number.isNaN(rating)) {
              data.answered_indexes = [rating];
              data.answered_ids = [rating];
              data.answers = `${rating}:"${rating}"`;
            }
          }

          return trackingEngine.questionTracker(question)(data);
        },
      },
      retryIds,
    );
  }

  public override finish(
    {
      finalId,
      points,
      isTimeExpired,
      correctAnswersCount,
      currentHash,
      previousHash,
    }: {
      finalId: string;
      points: number;
      isTimeExpired: boolean;
      correctAnswersCount: number;
      currentHash: string;
      previousHash: string | null;
    },
    retryIds?: string[],
  ) {
    const finalIndex = selectFinalIndex(this.poll, finalId);
    return super.finish(
      {
        finalId,
        finalIndex,
        isTimeExpired,
        correctAnswersCount,
        currentHash,
        previousHash,
        points,
      },
      retryIds,
    );
  }

  public override finalClick({ finalId }: { finalId: string }): void {
    const finalIndex = selectFinalIndex(this.poll, finalId);
    super.finalClick({ finalId, finalIndex });
  }

  public override trackMediaProgress({
    media_progress,
    questionId,
    answerIds,
  }: {
    media_progress: string;
    questionId: string;
    answerIds?: string[];
  }) {
    const { poll } = this;

    const questionIndex = selectQuestionIndex(poll, questionId);
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const question = poll.questions[questionIndex]!;

    super.trackMediaProgress({
      answerIds,
      questionId,
      questionIndex,
      media_progress,
      questionText: question.data.text || (question.data as { description?: string }).description || '',
      questionType: question.data.type,
    });
  }
}

function pollAuthProvider(poll: Poll) {
  const questions = poll.questions;
  const authStep = questions.find(isAuthQuestion);
  return authStep ? authStep.data.authProvider : null;
}
